import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { formatEatDate, formatEatTime } from '@wodi/domain';
import type { BreakGlassEntry } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Row,
  Section,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import { breakGlassPage, reviewBreakGlass } from '../api/privacy.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './labels-settings-page.tsx';
import styles from './privacy.module.css';

// Settings > Break-glass review (docs/plans/golive-privacy-tooling.md §2): every time someone
// opened a sensitive record they aren't cleared for, with their reason, for a reviewer to mark
// reviewed with a note. Opening the report is recorded in the audit log, by the server.
// TODO(legal-verify) P3: who reviews, and whether reasons show in full.

type State = 'unreviewed' | 'all';

function when(iso: string) {
  const at = new Date(iso);
  return `${formatEatDate(at)} ${formatEatTime(at)}`;
}

function ReviewDialog({ entry, onClose }: { entry: BreakGlassEntry; onClose: () => void }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [note, setNote] = useState('');
  const ready = note.trim().length >= 5;
  const mutation = useMutation({
    mutationFn: () => reviewBreakGlass(entry.id, note.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['privacy', 'break-glass'] });
      toasts.show({ title: 'Opening reviewed' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Mark reviewed"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <dl className={styles.prose}>
        <dt className={styles.meta}>Who and when</dt>
        <dd>{`${entry.actor.displayName}, ${when(entry.occurredAt)}`}</dd>
        <dt className={styles.meta}>Their reason</dt>
        <dd>{entry.reason ?? 'No reason recorded'}</dd>
      </dl>
      <TextArea
        label="Review note"
        description="What you checked and what you found."
        rows={3}
        value={note}
        onChange={setNote}
      />
      {mutation.error === null ? null : (
        <Notice tone="error">{errorText(mutation.error, 'The review was not saved.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready || !online}
          isPending={mutation.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mutation.mutate();
          }}
        >
          Mark reviewed
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Report({ state, userId }: { state: State; userId: string }) {
  const online = useOnline();
  const [reviewing, setReviewing] = useState<BreakGlassEntry | null>(null);
  const pages = useInfiniteQuery({
    queryKey: ['privacy', 'break-glass', state],
    queryFn: ({ pageParam }) => breakGlassPage(state, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Every read is audited: no refetch on focus.
    refetchOnWindowFocus: false,
  });
  if (pages.isPending) return <LoadingBlock label="Loading the break-glass openings" rows={4} />;
  if (pages.isError)
    return (
      <Notice tone="error" action={<Button onPress={() => void pages.refetch()}>Try again</Button>}>
        {errorText(pages.error, 'The break-glass openings could not be loaded. Try again.')}
      </Notice>
    );
  const items = pages.data.pages.flatMap((p) => p.items);
  const unreviewed = pages.data.pages[0]?.unreviewed ?? 0;
  return (
    <>
      <p className={styles.meta}>
        {unreviewed === 0
          ? 'Every opening has been reviewed.'
          : `${String(unreviewed)} ${unreviewed === 1 ? 'opening is' : 'openings are'} waiting for review.`}
      </p>
      {items.length === 0 ? (
        <p className={styles.meta}>
          {state === 'unreviewed' ? 'Nothing to review.' : 'No one has broken the glass yet.'}
        </p>
      ) : (
        <TableScroll>
          <Table aria-label="Break-glass openings" flat>
            <TableHeader>
              <Column isRowHeader>When</Column>
              <Column>Who</Column>
              <Column>Record</Column>
              <Column>Reason</Column>
              <Column>Review</Column>
            </TableHeader>
            <TableBody items={items}>
              {(e) => (
                <Row id={e.id}>
                  <Cell>
                    <span className={styles.numbers}>{when(e.occurredAt)}</span>
                  </Cell>
                  <Cell>{`${e.actor.displayName}${e.facility === null ? '' : `, ${e.facility.name}`}`}</Cell>
                  <Cell>
                    {e.visit === null ? (
                      '—'
                    ) : (
                      <span className={styles.mono}>{`Visit ${e.visit.visitNumber}`}</span>
                    )}
                  </Cell>
                  <Cell>
                    <span className={styles.prose}>{e.reason ?? '—'}</span>
                  </Cell>
                  <Cell>
                    {e.review !== null ? (
                      <span>
                        <Tag state="done">Reviewed</Tag>{' '}
                        <span className={styles.meta}>
                          {`${e.review.by.displayName}, ${when(e.review.at)}: ${e.review.note}`}
                        </span>
                      </span>
                    ) : e.actor.id === userId ? (
                      <span className={styles.meta}>Someone else reviews your own</span>
                    ) : (
                      <Button
                        dense
                        isDisabled={!online}
                        onPress={() => {
                          setReviewing(e);
                        }}
                      >
                        Mark reviewed
                      </Button>
                    )}
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      )}
      {pages.hasNextPage ? (
        <div className={styles.actions}>
          <Button
            isDisabled={!online}
            isPending={pages.isFetchingNextPage}
            pendingLabel="Loading…"
            onPress={() => void pages.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      ) : null}
      {reviewing === null ? null : (
        <ReviewDialog
          entry={reviewing}
          onClose={() => {
            setReviewing(null);
          }}
        />
      )}
    </>
  );
}

export function BreakGlassPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [state, setState] = useState<State>('unreviewed');
  if (session === null || !can(session, 'records.break_glass.review'))
    return (
      <SettingsPage title="Break-glass review">
        <Notice tone="info">You don&apos;t have permission to review break-glass openings.</Notice>
      </SettingsPage>
    );
  return (
    <SettingsPage title="Break-glass review">
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. The list shows what was last loaded; reviews wait for the
          connection.
        </Notice>
      ) : null}
      <Section
        title="Openings"
        actions={
          <SegmentedControl
            aria-label="Show"
            options={[
              { id: 'unreviewed', label: 'Not reviewed' },
              { id: 'all', label: 'All' },
            ]}
            value={state}
            onChange={(id) => {
              if (id === 'unreviewed' || id === 'all') setState(id);
            }}
          />
        }
      >
        <Report state={state} userId={session.user.id} />
      </Section>
    </SettingsPage>
  );
}
